import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  LoadingOverlay,
  PageHeader,
  PagePaper,
} from 'dashboard/components/common';

/**
 * The content library — everything a brand fills in once and reuses.
 *
 * This existed already as eleven separate menu items, which hid the only fact
 * that makes the system comprehensible: these are not pages, they are the
 * material pages are built from. A brand could not see what they had filled
 * in without visiting eleven screens, and nothing told them the list was
 * finite.
 *
 * Grouped rather than alphabetical. "Blog posts" and "News" belong together
 * because a brand thinking about one is usually thinking about the other, and
 * an A-Z list of eleven content types is a list you read rather than scan.
 *
 * Counts are the whole point of the screen: empty and filled have to be
 * distinguishable at a glance, because the next question is always "what have
 * I not done yet".
 */

const GROUP_ORDER = ['Writing', 'Work', 'Your story', 'People'];

function TypeCard({ type, onOpen }) {
  const empty = type.count === 0;
  const broken = type.available === false;

  return (
    <Box
      role='button'
      tabIndex={0}
      onClick={() => !broken && onOpen(type)}
      onKeyDown={(e) => {
        if (broken) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(type); }
      }}
      sx={{
        height: '100%',
        p: '16px',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        cursor: broken ? 'not-allowed' : 'pointer',
        opacity: broken ? 0.6 : 1,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': broken ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap='8px' mb='4px' flexWrap='wrap'>
        <Typography fontWeight='700' variant='body2'>{type.label}</Typography>
        {broken ? (
          <Chip size='small' color='warning' icon={<WarningAmberIcon fontSize='small' />} label='Unavailable' />
        ) : empty ? (
          <Chip size='small' variant='outlined' label='Empty' />
        ) : (
          <Chip
            size='small'
            color='success'
            icon={<CheckCircleIcon fontSize='small' />}
            label={type.single ? 'Filled' : type.count}
          />
        )}
      </Stack>
      <Typography variant='caption' color='text.secondary' display='block'>
        {type.describes}
      </Typography>
    </Box>
  );
}

export default function ContentLibrary() {
  const [selectedBrand] = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return;
    setLoading(true);
    API.getLibrarySummary({ brand_id: selectedBrand })
      .then(setData)
      .catch((err) => toast.error(err?.message || 'Could not load the content library'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => { load(); }, [load]);

  // The editors still live at their original URLs. Keeping them there means
  // no existing link, bookmark or in-progress work breaks while the library
  // becomes the way in.
  const open = (type) => navigate(`/dashboard/webpage/${type.key}`);

  const groups = GROUP_ORDER
    .map((name) => ({ name, types: (data?.types || []).filter((t) => t.group === name) }))
    .filter((g) => g.types.length > 0);

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Content library'
          subtitle='Everything you fill in once and reuse. Pages point at this material rather than storing their own copy, so editing here updates every page that shows it.'
          actions={
            data ? (
              <Chip
                variant='outlined'
                label={`${data.filled} of ${data.total} filled in`}
              />
            ) : null
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to see its content.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && !data} minHeight={240}>
            {data && (
              <Stack spacing='26px'>
                {groups.map((group) => (
                  <Box key={group.name}>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}
                      display='block'
                      mb='10px'
                    >
                      {group.name}
                    </Typography>
                    <Grid container spacing='10px'>
                      {group.types.map((t) => (
                        <Grid item xs={12} sm={6} md={4} key={t.key}>
                          <TypeCard type={t} onOpen={open} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Stack>
            )}
          </LoadingOverlay>
        )}
      </PagePaper>
    </Box>
  );
}
